import { useEffect, useState } from "react";
import { BookMarked, CircleCheck, CircleDashed, Quote } from "lucide-react";
import { getContextLibrary } from "../lib/api";
import { CaveatNote, EvidenceSection } from "../components/Evidence";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";

export default function ContextPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getContextLibrary().then(setData).catch(setErr);
  }, []);

  return (
    <div>
      <PageHeader overline="Context library" title="Sector material we cite but never compute with"
                  testId="context-header">
        These sources frame an answer; they never produce one.
      </PageHeader>

      {err && <ErrorBox error={err} />}
      {!data && !err && <Loading />}

      {data && (
        <div className="space-y-8">
          <div className="flex flex-wrap gap-x-10 gap-y-2 text-[13px] text-stone-600"
               data-testid="context-counts">
            <span><strong className="text-stone-900">{data.loaded}</strong> loaded and citable</span>
            <span><strong className="text-stone-900">{data.awaiting_upload}</strong> registered, awaiting re-upload</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(data.sources || []).map((s) => (
              <article key={s.id}
                       className="rounded-xl border border-stone-200 bg-white p-5 space-y-3.5"
                       data-testid={`context-source-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-[15px] font-semibold text-stone-900 leading-snug">
                      {s.title}
                    </h3>
                    <p className="text-[13px] text-stone-600 mt-0.5">
                      {s.publisher} · {s.year}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[12px] font-medium border shrink-0 ${
                    s.status === "loaded"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : "bg-stone-100 text-stone-600 border-stone-200"
                  }`}>
                    {s.status === "loaded"
                      ? <CircleCheck className="w-3 h-3" />
                      : <CircleDashed className="w-3 h-3" />}
                    {s.status}
                  </span>
                </div>

                <p className="text-[13px] text-stone-700 leading-relaxed">{s.coverage}</p>

                <div className="space-y-2">
                  <div className="flex gap-2 text-[13px] text-stone-700 leading-relaxed">
                    <BookMarked className="w-3.5 h-3.5 text-[#A01236] shrink-0 mt-0.5" />
                    <span>{s.why_it_helps}</span>
                  </div>
                  <div className="text-[12px] text-stone-600 leading-relaxed pl-[22px]">
                    Cannot support: {s.cannot_support}
                  </div>
                </div>

                {(s.claims || []).length > 0 && (
                  <EvidenceSection label="Citable claims" note={`${s.claims.length}`}>
                    <div className="space-y-2">
                      {s.claims.map((c, i) => (
                        <div key={i} className="rounded-md border border-stone-100 bg-stone-50 p-2.5">
                          <div className="flex gap-2">
                            <Quote className="w-3 h-3 text-stone-500 shrink-0 mt-1" />
                            <p className="text-[13px] text-stone-800 leading-relaxed">{c.claim}</p>
                          </div>
                          <div className="text-[12px] text-stone-500 num mt-1.5 pl-5">
                            {c.reference}
                          </div>
                        </div>
                      ))}
                    </div>
                  </EvidenceSection>
                )}
              </article>
            ))}
          </div>

          <CaveatNote title="The boundary this page keeps">{data.rule}</CaveatNote>
        </div>
      )}
    </div>
  );
}
